(function () {
  'use strict';

  const STORAGE_KEY = 'fm_lead_journey_v1';
  const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
  const HARD_TIMEOUT_MS = 4 * 60 * 60 * 1000;
  const MAX_PAGES = 25;
  const MAX_VISIBLE_MS = HARD_TIMEOUT_MS;
  const ACTIVITY_SAVE_THROTTLE_MS = 5000;
  const FLUSH_INTERVAL_MS = 15000;
  const PRIVACY_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000, 30000];

  let state = null;
  let memoryState = null;
  let currentPageId = '';
  let visibleSince = null;
  let trackingEnabled = false;
  let lastActivitySaveAt = 0;
  let privacyRetryIndex = 0;
  let privacyRetryTimer = null;
  let privacyCheckInFlight = false;

  function nowIso(timestamp) {
    return new Date(timestamp || Date.now()).toISOString();
  }

  function createId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }

    return 'fm_' + Date.now() + '_' + Math.random().toString(36).slice(2, 12);
  }

  function cleanText(value, maxLength) {
    if (typeof value !== 'string' && typeof value !== 'number') return '';
    return String(value)
      .replace(/[\u0000-\u001f\u007f]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, maxLength);
  }

  function cleanPath(value) {
    try {
      return new URL(String(value || '/'), window.location.origin).pathname.slice(0, 512) || '/';
    } catch (_) {
      const path = String(value || '/').split(/[?#]/)[0];
      return ('/' + path.replace(/^\/+/, '')).slice(0, 512);
    }
  }

  function getPageContext() {
    const raw = window.FotoMasterPageContext || {};
    return {
      path: cleanPath(raw.path || window.location.pathname),
      title: cleanText(raw.title || document.title, 200),
      page_type: cleanText(raw.page_type || '', 64),
      resource_handle: cleanText(raw.resource_handle || '', 255),
      product_title: cleanText(raw.product_title || '', 255),
      product_handle: cleanText(raw.product_handle || '', 255)
    };
  }

  function readStoredState() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch (_) {}
    return memoryState;
  }

  function saveState() {
    if (!state) return;
    memoryState = state;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_) {}
  }

  function clearState() {
    state = null;
    memoryState = null;
    currentPageId = '';
    visibleSince = null;
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (_) {}
  }

  function newState(timestamp) {
    return {
      version: 1,
      session_id: createId(),
      started_at_ms: timestamp,
      session_started_at: nowIso(timestamp),
      last_activity_ms: timestamp,
      total_page_views: 0,
      truncated: false,
      pages: []
    };
  }

  function stateExpired(candidate, timestamp) {
    if (!candidate || !Array.isArray(candidate.pages)) return true;
    const startedAt = Number(candidate.started_at_ms || 0);
    const lastActivity = Number(candidate.last_activity_ms || 0);
    return !startedAt || !lastActivity ||
      timestamp - lastActivity > IDLE_TIMEOUT_MS ||
      timestamp - startedAt > HARD_TIMEOUT_MS;
  }

  function appendCurrentPage(timestamp) {
    const context = getPageContext();
    currentPageId = createId();
    state.pages.push({
      id: currentPageId,
      path: context.path,
      title: context.title,
      page_type: context.page_type,
      resource_handle: context.resource_handle,
      product_title: context.product_title,
      product_handle: context.product_handle,
      entered_at: nowIso(timestamp),
      visible_ms: 0
    });
    state.total_page_views = Number(state.total_page_views || 0) + 1;

    if (state.pages.length > MAX_PAGES) {
      state.pages = state.pages.slice(-MAX_PAGES);
      state.truncated = true;
    }
  }

  function findCurrentPage() {
    if (!state || !Array.isArray(state.pages)) return null;
    return state.pages.find(function (page) {
      return page.id === currentPageId;
    }) || null;
  }

  function flushVisibleTime(timestamp) {
    if (!trackingEnabled || visibleSince === null || !state) return;
    const page = findCurrentPage();
    if (!page) return;

    const elapsed = Math.max(0, Math.min(MAX_VISIBLE_MS, timestamp - visibleSince));
    page.visible_ms = Math.min(MAX_VISIBLE_MS, Number(page.visible_ms || 0) + elapsed);
    visibleSince = document.visibilityState === 'visible' ? timestamp : null;
    saveState();
  }

  function resetOnCurrentPage(timestamp) {
    state = newState(timestamp);
    appendCurrentPage(timestamp);
    visibleSince = document.visibilityState === 'visible' ? timestamp : null;
    saveState();
  }

  function recordActivity() {
    if (!trackingEnabled || !state) return;
    const timestamp = Date.now();

    if (stateExpired(state, timestamp)) {
      resetOnCurrentPage(timestamp);
      return;
    }

    state.last_activity_ms = timestamp;
    if (timestamp - lastActivitySaveAt >= ACTIVITY_SAVE_THROTTLE_MS) {
      lastActivitySaveAt = timestamp;
      saveState();
    }
  }

  function startTracking() {
    if (trackingEnabled) return;
    const timestamp = Date.now();
    const stored = readStoredState();
    state = stateExpired(stored, timestamp) ? newState(timestamp) : stored;
    state.last_activity_ms = timestamp;
    appendCurrentPage(timestamp);
    trackingEnabled = true;
    visibleSince = document.visibilityState === 'visible' ? timestamp : null;
    saveState();
  }

  function stopTrackingAndClear() {
    trackingEnabled = false;
    clearState();
  }

  function pauseTracking() {
    if (trackingEnabled) {
      flushVisibleTime(Date.now());
    }

    trackingEnabled = false;
    visibleSince = null;
  }

  function readAnalyticsPermission() {
    if (!window.ShopifyConsent) {
      return Promise.resolve({ status: 'unavailable', allowed: false });
    }

    if (typeof window.ShopifyConsent.getState === 'function') {
      return window.ShopifyConsent.getState().then(function (privacyState) {
        if (!privacyState || privacyState.apiAvailable !== true) {
          return { status: 'unavailable', allowed: false };
        }

        if (privacyState.processingAllowed && privacyState.processingAllowed.analytics === true) {
          return { status: 'granted', allowed: true };
        }

        const visitorConsent = privacyState.visitorConsent || {};
        const analyticsChoice = visitorConsent.analytics;
        const explicitlyDenied = analyticsChoice === 'no' || analyticsChoice === false;
        return {
          status: explicitlyDenied ? 'denied' : 'required',
          allowed: false
        };
      }).catch(function () {
        return { status: 'unavailable', allowed: false };
      });
    }

    if (typeof window.ShopifyConsent.canAnalyze !== 'function') {
      return Promise.resolve({ status: 'unavailable', allowed: false });
    }

    return window.ShopifyConsent.canAnalyze().then(function (allowed) {
      return {
        status: allowed ? 'granted' : 'required',
        allowed: allowed === true
      };
    }).catch(function () {
      return { status: 'unavailable', allowed: false };
    });
  }

  function clearPrivacyRetry() {
    if (privacyRetryTimer !== null) {
      window.clearTimeout(privacyRetryTimer);
      privacyRetryTimer = null;
    }
  }

  function schedulePrivacyRetry() {
    if (privacyRetryTimer !== null || privacyRetryIndex >= PRIVACY_RETRY_DELAYS_MS.length) return;

    const delay = PRIVACY_RETRY_DELAYS_MS[privacyRetryIndex];
    privacyRetryIndex += 1;
    privacyRetryTimer = window.setTimeout(function () {
      privacyRetryTimer = null;
      synchronizeConsent();
    }, delay);
  }

  function synchronizeConsent() {
    if (privacyCheckInFlight) return;
    privacyCheckInFlight = true;

    readAnalyticsPermission().then(function (permission) {
      if (permission.allowed) {
        clearPrivacyRetry();
        privacyRetryIndex = 0;
        startTracking();
      } else if (permission.status === 'unavailable') {
        // Fail closed while Shopify is unavailable, but retain any journey
        // that was already collected with permission and retry in the
        // background so eligible visitors are not lost to a slow API load.
        pauseTracking();
        schedulePrivacyRetry();
      } else {
        clearPrivacyRetry();
        privacyRetryIndex = 0;
        stopTrackingAndClear();
      }
    }).catch(function () {
      pauseTracking();
      schedulePrivacyRetry();
    }).then(function () {
      privacyCheckInFlight = false;
    });
  }

  function normalizeCatalog(catalog) {
    return (Array.isArray(catalog) ? catalog : [])
      .map(function (item) {
        return {
          handle: cleanText(item && item.handle, 255).toLowerCase(),
          title: cleanText(item && item.title, 255)
        };
      })
      .filter(function (item) {
        return item.handle && item.title;
      })
      .sort(function (left, right) {
        return right.handle.length - left.handle.length;
      });
  }

  function escapeRegex(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function productForPage(page, catalog) {
    if (!page) return null;
    const explicitTitle = cleanText(page.product_title || '', 255);
    const explicitHandle = cleanText(page.product_handle || '', 255).toLowerCase();
    if (explicitTitle) {
      return { title: explicitTitle, handle: explicitHandle };
    }

    const path = cleanPath(page.path || '').toLowerCase();
    const resourceHandle = cleanText(page.resource_handle || '', 255).toLowerCase();
    for (const product of catalog) {
      const boundaryPattern = new RegExp('(?:^|[-/])' + escapeRegex(product.handle) + '(?:$|[-/])');
      if (resourceHandle === product.handle || boundaryPattern.test(resourceHandle) || boundaryPattern.test(path)) {
        return { title: product.title, handle: product.handle };
      }
    }

    return null;
  }

  function enrichPages(pages, catalog) {
    return pages.map(function (page) {
      const cleanPage = {
        path: cleanPath(page.path || ''),
        title: cleanText(page.title || '', 200),
        page_type: cleanText(page.page_type || '', 64),
        resource_handle: cleanText(page.resource_handle || '', 255),
        product_title: cleanText(page.product_title || '', 255),
        product_handle: cleanText(page.product_handle || '', 255),
        entered_at: cleanText(page.entered_at || '', 64),
        visible_ms: Math.max(0, Math.min(MAX_VISIBLE_MS, Number(page.visible_ms || 0)))
      };
      const product = productForPage(cleanPage, catalog);
      if (product) {
        cleanPage.product_title = product.title;
        cleanPage.product_handle = product.handle;
      }
      return cleanPage;
    });
  }

  async function getSnapshot(productCatalog) {
    const permission = await readAnalyticsPermission();
    if (!permission.allowed) {
      if (permission.status === 'unavailable') {
        pauseTracking();
        schedulePrivacyRetry();
      } else {
        stopTrackingAndClear();
      }

      return {
        consent_status: permission.status,
        captured_at: nowIso(),
        total_visible_ms: 0,
        total_page_views: 0,
        truncated: false,
        pages: []
      };
    }

    const timestamp = Date.now();
    if (!trackingEnabled) startTracking();
    else if (stateExpired(state, timestamp)) resetOnCurrentPage(timestamp);
    flushVisibleTime(timestamp);
    const catalog = normalizeCatalog(productCatalog);
    const pages = enrichPages(Array.isArray(state.pages) ? state.pages.slice(-MAX_PAGES) : [], catalog);
    const totalVisibleMs = Math.min(MAX_VISIBLE_MS, pages.reduce(function (sum, page) {
      return sum + page.visible_ms;
    }, 0));

    return {
      consent_status: 'granted',
      session_started_at: cleanText(state.session_started_at || '', 64),
      captured_at: nowIso(),
      total_visible_ms: totalVisibleMs,
      total_page_views: Math.max(pages.length, Number(state.total_page_views || 0)),
      truncated: Boolean(state.truncated),
      pages: pages
    };
  }

  async function buildSubmissionContext(selectedProduct, productCatalog, productSelection) {
    const catalog = normalizeCatalog(productCatalog);
    const journey = await getSnapshot(catalog);
    const pageContext = getPageContext();
    const currentProduct = productForPage(pageContext, catalog);
    if (currentProduct) {
      pageContext.product_title = currentProduct.title;
      pageContext.product_handle = currentProduct.handle;
    }

    const selected = cleanText(selectedProduct || '', 255);
    let productResolution;
    if (selected) {
      productResolution = {
        value: selected,
        source: 'unverified',
        source_page: pageContext.path
      };
      if (productSelection && productSelection.value === selected
          && ['selected', 'prefilled', 'unverified'].includes(productSelection.source)) {
        productResolution = { ...productSelection, value: selected };
      }
    } else if (currentProduct) {
      productResolution = {
        value: currentProduct.title,
        source: 'current_page',
        source_page: pageContext.path
      };
    } else {
      let previousProduct = null;
      for (let index = journey.pages.length - 1; index >= 0; index -= 1) {
        const candidate = productForPage(journey.pages[index], catalog);
        if (candidate) {
          previousProduct = {
            product: candidate,
            path: journey.pages[index].path
          };
          break;
        }
      }

      productResolution = previousProduct
        ? {
            value: previousProduct.product.title,
            source: 'previous_product_page',
            source_page: previousProduct.path
          }
        : {
            value: 'General Inquiry',
            source: 'general',
            source_page: pageContext.path
          };
    }

    return {
      page_context: pageContext,
      product_resolution: productResolution,
      journey: journey
    };
  }

  document.addEventListener('visibilitychange', function () {
    const timestamp = Date.now();
    if (document.visibilityState === 'hidden') {
      flushVisibleTime(timestamp);
      visibleSince = null;
    } else if (trackingEnabled) {
      if (stateExpired(state, timestamp)) resetOnCurrentPage(timestamp);
      else visibleSince = timestamp;
      recordActivity();
    }
  });
  window.addEventListener('pagehide', function () {
    flushVisibleTime(Date.now());
  });
  ['click', 'keydown', 'touchstart', 'scroll'].forEach(function (eventName) {
    window.addEventListener(eventName, recordActivity, { passive: true });
  });
  window.setInterval(function () {
    if (trackingEnabled && document.visibilityState === 'visible') {
      flushVisibleTime(Date.now());
    }
  }, FLUSH_INTERVAL_MS);

  if (window.ShopifyConsent && typeof window.ShopifyConsent.onConsentChange === 'function') {
    window.ShopifyConsent.onConsentChange(synchronizeConsent);
  } else {
    document.addEventListener('visitorConsentCollected', synchronizeConsent);
    document.addEventListener('trackingConsentAccepted', synchronizeConsent);
  }
  synchronizeConsent();

  window.FotoMasterLeadJourney = {
    getPageContext: getPageContext,
    getSnapshot: getSnapshot,
    buildSubmissionContext: buildSubmissionContext
  };
})();
